import { useCallback, useEffect, useRef } from 'react';
import { useAppData } from '../contexts/AppDataContext';

/**
 * Custom hook to integrate ESDL monitoring alerts with the global notification system
 * @param {Object} dashboardData - Current ESDL dashboard data
 * @param {Object} resourceStats - Current ESDL resource stats
 * @returns {Object} - Utility functions for working with ESDL notifications
 */
const useEsdlNotifications = (dashboardData, resourceStats) => {
  const { 
    alerts = [], 
    fetchAlerts, 
    createJiraTicket,  // Add JIRA ticket creation function
    jiraConfig         // Get JIRA configuration for assignees
  } = useAppData();
  
  // Add refs to track state and prevent redundant updates
  const isUpdateInProgress = useRef(false);
  const lastProcessedData = useRef(null);
  const lastUpdateTime = useRef(0);
  const ticketTracker = useRef({});
  const logThrottleRef = useRef(0);

  // Helper function to throttle console logs
  const throttledLog = useCallback((message, data) => {
    const now = Date.now();
    // Only log once every 5 seconds
    if (now - logThrottleRef.current > 5000) {
      console.log(message, data);
      logThrottleRef.current = now;
    }
  }, []);

  /**
   * Process dashboard data to generate alerts for critical metrics
   */
  const processEsdlAlerts = useCallback((data) => {
    if (!data) return [];
    
    const newAlerts = [];
    const now = new Date().toISOString();
    
    // Check system health metrics
    if (data.system_health) {
      // Check for critical CPU usage across hosts
      if (data.system_health.critical_hosts?.cpu?.length > 0) {
        data.system_health.critical_hosts.cpu.forEach(hostname => {
          newAlerts.push({
            hostname,
            region: data.hosts?.find(h => h.hostname === hostname)?.region || '',
            type: 'cpu',
            value: data.system_health.avg_cpu_load,
            threshold: 85,
            message: `High CPU usage on ${hostname}`,
            timestamp: now
          });
        });
      }
      
      // Check for critical memory usage across hosts
      if (data.system_health.critical_hosts?.memory?.length > 0) {
        data.system_health.critical_hosts.memory.forEach(hostname => {
          newAlerts.push({
            hostname,
            region: data.hosts?.find(h => h.hostname === hostname)?.region || '',
            type: 'memory',
            value: data.system_health.avg_memory_percent,
            threshold: 85,
            message: `High memory usage on ${hostname}`,
            timestamp: now
          });
        });
      }
      
      // Check for critical disk usage across hosts
      if (data.system_health.critical_hosts?.disk?.length > 0) {
        data.system_health.critical_hosts.disk.forEach(hostname => {
          newAlerts.push({
            hostname,
            region: data.hosts?.find(h => h.hostname === hostname)?.region || '',
            type: 'disk',
            value: data.system_health.avg_disk_percent,
            threshold: 85,
            message: `High disk usage on ${hostname}`,
            timestamp: now
          });
        });
      }
    }
    
    return newAlerts;
  }, []);

  /**
   * Create JIRA tickets for critical alerts based on region assignment
   */
  const createJiraTickets = useCallback(async (alerts) => {
    if (!createJiraTicket || !jiraConfig || !jiraConfig.jira_enabled || !alerts.length) {
      return;
    }

    const regionAssignees = jiraConfig.region_assignees || {};
    
    // Use ref-based ticket tracker instead of local variable
    const ticketsCreated = [];
    
    for (const alert of alerts) {
      // Create a unique key for this alert to avoid duplicates
      const alertKey = `${alert.type}-${alert.hostname}-${new Date().toISOString().substring(0, 10)}`;
      
      // Skip if we already created a ticket for this issue today
      if (ticketTracker.current[alertKey]) {
        continue;
      }
      
      // Get the appropriate assignee for this region
      const assignee = regionAssignees[alert.region] || null;
      
      // Prepare ticket data
      const ticketData = {
        summary: `[ESDL Alert] ${alert.type.toUpperCase()} - ${alert.message}`,
        description: `
Critical ${alert.type} alert detected:
- Host: ${alert.hostname}
- Region: ${alert.region}
- Value: ${alert.value}%
- Threshold: ${alert.threshold}%
- Time: ${new Date(alert.timestamp).toLocaleString()}

This ticket was automatically created from the ESDL Monitoring Dashboard.
        `,
        type: 'Incident',
        priority: 'High',
        assignee: assignee,
        components: ['ESDL Monitoring']
      };
      
      try {
        // Create the ticket
        const result = await createJiraTicket(ticketData);
        throttledLog(`Created JIRA ticket for ${alert.type} alert on ${alert.hostname}:`, result);
        
        // Mark as created
        ticketTracker.current[alertKey] = true;
        ticketsCreated.push(alertKey);
      } catch (error) {
        console.error(`Failed to create JIRA ticket for ${alert.type} alert:`, error);
      }
    }
    
    return ticketsCreated;
  }, [createJiraTicket, jiraConfig, throttledLog]);

  /**
   * Check if we need to update alerts based on new data
   */
  const hasNewCriticalHosts = useCallback((sourceData) => {
    if (!sourceData) return false;
    
    // Skip if no critical hosts
    if (!sourceData.host_counts?.criticalHosts && 
        !sourceData.system_health?.critical_hosts) {
      return false;
    }
    
    // Get critical hosts
    const criticalHosts = sourceData.host_counts?.criticalHosts || 
                         sourceData.system_health?.critical_hosts;
    
    // Check if we have any critical hosts
    const hasCritical = (
      (criticalHosts?.cpu?.length > 0) || 
      (criticalHosts?.memory?.length > 0) || 
      (criticalHosts?.disk?.length > 0)
    );
    
    // Skip if no critical hosts
    if (!hasCritical) return false;
    
    // Enforce a minimum time between updates (30 seconds)
    const now = Date.now();
    if (now - lastUpdateTime.current < 30000) {
      return false;
    }
    
    // Prepare a fingerprint from the critical data that matters
    const dataFingerprint = {
      cpu: criticalHosts?.cpu?.sort() || [],
      memory: criticalHosts?.memory?.sort() || [],
      disk: criticalHosts?.disk?.sort() || []
    };
    
    // Check if data has changed since last update
    const dataFingerprintStr = JSON.stringify(dataFingerprint);
    if (lastProcessedData.current === dataFingerprintStr) {
      return false;
    }
    
    // Data has changed and enough time has passed, update last processed data
    lastProcessedData.current = dataFingerprintStr;
    lastUpdateTime.current = now;
    return true;
  }, []);

  /**
   * Update the global notification system with ESDL alerts
   */
  const updateNotifications = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isUpdateInProgress.current) {
      return;
    }
    
    // Use dashboardData first if available (it has more info)
    const sourceData = dashboardData && dashboardData.host_counts ? dashboardData : resourceStats;
    
    // Skip if no data or no critical hosts
    if (!hasNewCriticalHosts(sourceData)) {
      return;
    }
    
    // Mark update as in progress
    isUpdateInProgress.current = true;
    
    try {
      // Generate alerts from critical hosts
      const criticalHostsAlerts = [];
      const now = new Date().toISOString();
      
      // Check if we have dashboardData with host_counts
      if (sourceData?.host_counts?.criticalHosts) {
        const criticalHosts = sourceData.host_counts.criticalHosts;
        
        // CPU critical hosts
        if (criticalHosts.cpu && criticalHosts.cpu.length > 0) {
          criticalHosts.cpu.forEach(hostname => {
            // Find the host data for more details
            const hostData = sourceData.hosts?.find(h => h.hostname === hostname);
            criticalHostsAlerts.push({
              hostname,
              region: hostData?.region || '',
              type: 'cpu',
              value: hostData?.system?.cpu?.load_1min || sourceData.system_health?.avg_cpu_load || 90,
              threshold: 80,
              message: `High CPU usage on ${hostname}`,
              timestamp: now
            });
          });
        }
        
        // Memory critical hosts
        if (criticalHosts.memory && criticalHosts.memory.length > 0) {
          criticalHosts.memory.forEach(hostname => {
            // Find the host data for more details
            const hostData = sourceData.hosts?.find(h => h.hostname === hostname);
            criticalHostsAlerts.push({
              hostname,
              region: hostData?.region || '',
              type: 'memory',
              value: hostData?.system?.memory?.percent || sourceData.system_health?.avg_memory_percent || 90,
              threshold: 85,
              message: `High memory usage on ${hostname}`,
              timestamp: now
            });
          });
        }
        
        // Disk critical hosts
        if (criticalHosts.disk && criticalHosts.disk.length > 0) {
          criticalHosts.disk.forEach(hostname => {
            // Find the host data for more details
            const hostData = sourceData.hosts?.find(h => h.hostname === hostname);
            criticalHostsAlerts.push({
              hostname,
              region: hostData?.region || '',
              type: 'disk',
              value: hostData?.system?.disk?.percent || sourceData.system_health?.avg_disk_percent || 95,
              threshold: 90,
              message: `High disk usage on ${hostname}`,
              timestamp: now
            });
          });
        }
      } else {
        // Fallback to older method
        const dashboardAlerts = processEsdlAlerts(dashboardData);
        const resourceAlerts = processEsdlAlerts({
          system_health: {
            critical_hosts: resourceStats?.summary?.critical_hosts,
            avg_cpu_load: resourceStats?.summary?.avg_cpu_load,
            avg_memory_percent: resourceStats?.summary?.avg_memory_percent,
            avg_disk_percent: resourceStats?.summary?.avg_disk_percent
          },
          hosts: resourceStats?.hosts
        });
        
        // Combine alerts from both sources
        criticalHostsAlerts.push(...dashboardAlerts, ...resourceAlerts);
      }
      
      // Only update if we found critical issues
      if (criticalHostsAlerts.length > 0) {
        throttledLog('Found critical hosts to alert on:', criticalHostsAlerts.length);
        
        // Only call fetchAlerts once per 60 seconds to avoid infinite loop
        // This will update the global alert state
        if (typeof fetchAlerts === 'function') {
          // Create JIRA tickets for these alerts with region-based assignment
          await createJiraTickets(criticalHostsAlerts);
        }
      }
    } finally {
      // Clear the in-progress flag
      isUpdateInProgress.current = false;
    }
  }, [dashboardData, resourceStats, processEsdlAlerts, fetchAlerts, createJiraTickets, hasNewCriticalHosts, throttledLog]);

  // Add a strong throttle to control effect updates - only check every minute at most
  const depsChangedTimeRef = useRef(0);
  const effectRunCountRef = useRef(0);

  // Update notifications when dashboard data or resource stats change significantly
  // This effect is heavily throttled to prevent performance issues
  useEffect(() => {
    // Early exit if the hook is being reinstantiated
    if (!dashboardData && !resourceStats) return;
    
    // Enforce minimum 60 seconds between automatic checks
    const now = Date.now();
    if (now - depsChangedTimeRef.current < 60000) {
      return;
    }
    
    // Record the time we considered checking
    depsChangedTimeRef.current = now;
    
    // Skip if either dependency isn't valid
    if (!(dashboardData?.system_health || resourceStats?.summary)) {
      return;
    }
    
    // Skip the first several renders to prevent initial storm of checks
    if (effectRunCountRef.current < 2) {
      effectRunCountRef.current++;
      return;
    }

    // Use a very long debounce time to prevent too frequent checks
    const debounceTimeout = setTimeout(() => {
      // Still check the throttle before proceeding
      if (Date.now() - lastUpdateTime.current < 30000) {
        throttledLog('Skipping auto-update - too soon since last check', null);
        return;
      }
      
      throttledLog('Running throttled notification check', null);
      updateNotifications();
    }, 5000); // 5 seconds debounce
    
    return () => clearTimeout(debounceTimeout);
  }, [dashboardData, resourceStats, updateNotifications, throttledLog]);

  // Force a notification check on mount, but only once
  const initialCheckDoneRef = useRef(false);
  useEffect(() => {
    // Skip if already done the initial check
    if (initialCheckDoneRef.current) return;
    
    // Skip if we don't have valid data
    if (!(dashboardData?.system_health || resourceStats?.summary)) return;
    
    // Wait a while to ensure everything is settled
    const timer = setTimeout(() => {
      throttledLog("Running initial notification check", null);
      updateNotifications();
      initialCheckDoneRef.current = true;
    }, 8000); // Wait 8 seconds after mounting to ensure data is fully loaded
    
    return () => clearTimeout(timer);
  }, [dashboardData, resourceStats, updateNotifications, throttledLog]);

  return {
    processEsdlAlerts,
    updateNotifications,
    createJiraTickets,
    currentAlerts: alerts.filter(alert => 
      ['cpu', 'memory', 'disk'].includes(alert.type)
    )
  };
};

export default useEsdlNotifications; 